import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Form, Modal, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { fetchAccounts, fetchStoreAccountSettings } from "../../api/api";
import { useTransactions } from "../../contextjs/TransactionContext";
import { useUser } from "../../UserContext";
import { AccountingScopeSelect, useAccountingScope } from "./accountingscope";

ChartJS.register(ArcElement, Tooltip, Legend);

const CurrentAssets = () => {
    const scope = useAccountingScope();
    const { user } = useUser();
    const { transactions, createNewTransaction, loadTransactions, totalCount } = useTransactions();

    const [currentAssetsAccounts, setCurrentAssetsAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [error, setError] = useState("");
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const entriesPerPage = 20;

    const canTransfer = Boolean(
        user?.is_superuser ||
        user?.groups?.includes("Admin") ||
        user?.groups?.includes("Administrator")
    );

    const [transfer, setTransfer] = useState({
        from_account: "",
        to_account: "",
        amount: "",
        transaction_date: new Date().toISOString().split("T")[0],
        particulars: "",
    });

    const loadAssetAccounts = async () => {
        if (scope.loadingScope || !scope.storeId) return;

        setLoadingAccounts(true);
        setError("");

        try {
            const filters = scope.storeId === "all" ? {} : { store_id: scope.storeId };

            const [accountData, settings] = await Promise.all([
                fetchAccounts(filters),
                scope.storeId === "all"
                    ? Promise.resolve([])
                    : fetchStoreAccountSettings({ store_id: scope.storeId }),
            ]);

            const accounts = Array.isArray(accountData)
                ? accountData
                : Array.isArray(accountData?.results)
                    ? accountData.results
                    : [];

            const enabledIds = settings.length
                ? new Set(
                    settings
                        .filter((setting) => setting.is_enabled)
                        .map((setting) => String(setting.account))
                )
                : null;

            setCurrentAssetsAccounts(
                accounts.filter((account) => (
                    account.is_current_asset &&
                    ["Asset", "Receivable"].includes(account.account_type_details?.account_type_name) &&
                    (enabledIds === null || enabledIds.has(String(account.id)))
                ))
            );
        } catch (loadError) {
            console.error("Error loading current asset accounts", loadError);
            setError("Failed to load current asset balances.");
            setCurrentAssetsAccounts([]);
        } finally {
            setLoadingAccounts(false);
        }
    };

    useEffect(() => {
        loadAssetAccounts();
    }, [scope.loadingScope, scope.storeId]);

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId) return;

        const filters = { reference: "Transfer" };

        if (scope.storeId !== "all") filters.store_id = scope.storeId;
        if (startDate) filters.start_date = startDate;
        if (endDate) filters.end_date = endDate;

        loadTransactions(currentPage, entriesPerPage, filters);
    }, [scope.loadingScope, scope.storeId, currentPage, startDate, endDate, loadTransactions]);

    useEffect(() => {
        setCurrentPage(1);
    }, [scope.storeId, startDate, endDate]);

    const assetData = useMemo(() => {
        const data = {};
        currentAssetsAccounts.forEach((account) => {
            data[account.account_name] = Number(account.computed_balance || 0);
        });
        return data;
    }, [currentAssetsAccounts]);

    const totalCurrentAssets = Object.values(assetData).reduce((sum, value) => sum + value, 0);

    const currentTransfers = transactions.filter((transaction) => transaction.reference === "Transfer");
    const totalPages = Math.max(1, Math.ceil(totalCount / entriesPerPage));
    const selectedStoreForTransfer = scope.storeId !== "all";

    const pieData = {
        labels: Object.keys(assetData),
        datasets: [
            {
                data: Object.values(assetData).map((value) => Math.abs(value)),
                backgroundColor: ["#dc3545", "#0d6efd", "#ffc107", "#6f42c1", "#198754", "#0dcaf0"],
            },
        ],
    };

    const getAccountBalance = (accountId) => {
        const account = currentAssetsAccounts.find((item) => String(item.id) === String(accountId));
        if (!account) return "";

        const balance = Number(account.computed_balance || 0);
        const label = balance >= 0 ? "Dr." : "Cr.";

        return `${label} Ksh ${Math.abs(balance).toLocaleString()}`;
    };

    const handleTransferSubmit = async (event) => {
        event.preventDefault();

        const amount = Number(transfer.amount);

        if (!selectedStoreForTransfer) {
            setError("Select a specific store before recording a fund transfer.");
            return;
        }

        if (transfer.from_account === transfer.to_account) {
            setError("The source and destination accounts must be different.");
            return;
        }

        if (!transfer.from_account || !transfer.to_account || amount <= 0 || !transfer.transaction_date) {
            setError("Complete both accounts, the date, and an amount greater than zero.");
            return;
        }

        try {
            await createNewTransaction({
                transaction_date: transfer.transaction_date,
                reference: "Transfer",
                description: transfer.particulars,
                amount,
                store_id: Number(scope.storeId),
                items: [
                    {
                        account: transfer.from_account,
                        item_description: transfer.particulars,
                        amount,
                        transaction_type: "CREDIT",
                    },
                    {
                        account: transfer.to_account,
                        item_description: transfer.particulars,
                        amount,
                        transaction_type: "DEBIT",
                    },
                ],
            });

            setTransfer({
                from_account: "",
                to_account: "",
                amount: "",
                transaction_date: new Date().toISOString().split("T")[0],
                particulars: "",
            });

            setShowTransferModal(false);
            setCurrentPage(1);

            await Promise.all([
                loadAssetAccounts(),
                loadTransactions(1, entriesPerPage, {
                    reference: "Transfer",
                    store_id: scope.storeId,
                }),
            ]);
        } catch (submitError) {
            console.error("Error recording fund transfer", submitError);
            setError(submitError?.response?.data?.detail || "Failed to record the fund transfer.");
        }
    };

    return (
        <div className="container mt-5">
            <h1>Current Assets</h1>

            {scope.isAdmin && (
                <Row className="mb-3">
                    <Col md={5}>
                        <AccountingScopeSelect scope={scope} label="Current Asset Scope" />
                    </Col>
                </Row>
            )}

            {scope.scopeError && <Alert variant="danger">{scope.scopeError}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            <Row className="mt-4">
                <Col md={6}>
                    <Card>
                        <Card.Body>
                            <Card.Title>{scope.scopeLabel} Current Asset Breakdown</Card.Title>
                            {loadingAccounts ? <Spinner animation="border" /> : <Pie data={pieData} />}
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="text-white bg-info mb-4">
                        <Card.Body>
                            <Card.Title>{scope.scopeLabel} Current Asset Balances</Card.Title>

                            {loadingAccounts ? (
                                <Spinner animation="border" size="sm" />
                            ) : currentAssetsAccounts.length > 0 ? (
                                currentAssetsAccounts.map((account) => (
                                    <Card.Text key={account.id}>
                                        {account.account_name}: Ksh {Number(account.computed_balance || 0).toLocaleString()}
                                    </Card.Text>
                                ))
                            ) : (
                                <Card.Text>No current asset accounts configured for this scope.</Card.Text>
                            )}

                            <Card.Text className="text-black fw-bold">
                                Total current assets: Ksh {totalCurrentAssets.toLocaleString()}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {canTransfer && selectedStoreForTransfer && (
                <Button variant="primary" onClick={() => setShowTransferModal(true)} className="mb-4">
                    Add Transfer
                </Button>
            )}

            {canTransfer && !selectedStoreForTransfer && (
                <Alert variant="info">
                    Select a specific store to transfer funds between its current asset accounts.
                </Alert>
            )}

            <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Transfer Funds in {scope.scopeLabel}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form onSubmit={handleTransferSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Transfer Funds From</Form.Label>
                                    <Form.Select
                                        value={transfer.from_account}
                                        onChange={(event) => setTransfer({ ...transfer, from_account: event.target.value })}
                                    >
                                        <option value="">Select account</option>
                                        {currentAssetsAccounts.map((account) => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {transfer.from_account && (
                                        <Form.Text>Current balance: {getAccountBalance(transfer.from_account)}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Transfer Funds To</Form.Label>
                                    <Form.Select
                                        value={transfer.to_account}
                                        onChange={(event) => setTransfer({ ...transfer, to_account: event.target.value })}
                                    >
                                        <option value="">Select account</option>
                                        {currentAssetsAccounts.map((account) => (
                                            <option key={account.id} value={account.id}>
                                                {account.account_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {transfer.to_account && (
                                        <Form.Text>Current balance: {getAccountBalance(transfer.to_account)}</Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={6}>
                                <Form.Label>Amount</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={transfer.amount}
                                    onChange={(event) => setTransfer({ ...transfer, amount: event.target.value })}
                                />
                            </Col>

                            <Col md={6}>
                                <Form.Label>Transfer Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={transfer.transaction_date}
                                    onChange={(event) => setTransfer({ ...transfer, transaction_date: event.target.value })}
                                />
                            </Col>
                        </Row>

                        <Form.Group className="mt-3">
                            <Form.Label>Particulars</Form.Label>
                            <Form.Control
                                value={transfer.particulars}
                                onChange={(event) => setTransfer({ ...transfer, particulars: event.target.value })}
                            />
                        </Form.Group>

                        <Button type="submit" className="mt-3 float-end">
                            Save Transfer
                        </Button>
                    </Form>
                </Modal.Body>
            </Modal>

            <Row className="mb-4">
                <Col md={5}>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                    />
                </Col>

                <Col md={5}>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                    />
                </Col>
            </Row>

            <h2>Transfers</h2>

            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>From Account</th>
                        <th>To Account</th>
                        <th>Amount</th>
                        <th>Particulars</th>
                    </tr>
                </thead>

                <tbody>
                    {currentTransfers.map((transaction) => {
                        const debit = transaction.items.find((item) => item.transaction_type === "DEBIT");
                        const credit = transaction.items.find((item) => item.transaction_type === "CREDIT");

                        return (
                            <tr key={transaction.id}>
                                <td>{new Date(transaction.transaction_date).toLocaleDateString("en-GB")}</td>
                                <td>{credit?.account_details?.account_name}</td>
                                <td>{debit?.account_details?.account_name}</td>
                                <td>Ksh {Number(transaction.amount || 0).toLocaleString()}</td>
                                <td>{transaction.description}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <Pagination className="justify-content-center">
                <Pagination.Prev
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                />
                <Pagination.Item active>{currentPage}</Pagination.Item>
                <Pagination.Next
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage >= totalPages}
                />
            </Pagination>
        </div>
    );
};

export default CurrentAssets;